// Event Tracking - Core event tracking across analytics platforms
// Extracted from Analytics.js for better separation of concerns

import mixpanel from 'mixpanel-browser';
import Clarity from '@microsoft/clarity';
import { debugLog } from '../../utils/debugUtils';
import {
  isPreviewOrInternalDomain,
  shouldInitializeAnalytics,
} from '../../utils/environmentUtils';
import {
  logCriticalAnalyticsError,
  trackAnalyticsErrorPattern,
} from '../../utils/debugUtils';

// Tracks events across all analytics platforms
export const trackEvent = (eventName, eventParams = {}) => {
  // Early return for any development or preview environments
  if (typeof window === 'undefined' || isPreviewOrInternalDomain()) {
    debugLog(
      'Analytics disabled for development environment:',
      eventName,
      eventParams
    );
    return;
  }

  // Early return if analytics are disabled by environment
  if (!shouldInitializeAnalytics()) {
    debugLog(
      'Analytics disabled for this environment:',
      eventName,
      eventParams
    );
    return;
  }

  const enrichedParams = {
    ...eventParams,
    page_location: window.location.href,
    page_title: document.title,
  };

  // Track in Google Analytics
  trackGoogleAnalyticsEvent(eventName, enrichedParams);

  // Track in Mixpanel
  trackMixpanelEvent(eventName, enrichedParams);

  // Track in Clarity
  trackClarityEvent(eventName, enrichedParams);
};

// Track event in Google Analytics
const trackGoogleAnalyticsEvent = (eventName, eventParams) => {
  if (window.gtag) {
    try {
      window.gtag('event', eventName, eventParams);
      debugLog('✅ Google Analytics event tracked:', eventName);
      // Track success pattern
      trackAnalyticsErrorPattern('google_analytics_tracking', true);
    } catch (error) {
      debugLog('❌ Google Analytics tracking failed:', error);
      // Track failure pattern and log critical errors
      trackAnalyticsErrorPattern('google_analytics_tracking', false);

      // Send critical tracking failures to Netlify function for monitoring
      logCriticalAnalyticsError(
        'google_analytics_tracking_failed',
        `Google Analytics event tracking failed: ${error.message}`,
        {
          eventName,
          error: error.message,
          stack: error.stack,
          params: eventParams,
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        },
        'error'
      );
    }
  } else {
    debugLog('⚠️ Google Analytics (gtag) not available for event:', eventName);
    // Track unavailability pattern
    trackAnalyticsErrorPattern('google_analytics_availability', false);
  }
};

// Track event in Mixpanel with queue fallback
const trackMixpanelEvent = (eventName, eventParams) => {
  try {
    if (
      typeof mixpanel !== 'undefined' &&
      mixpanel &&
      typeof mixpanel.track === 'function'
    ) {
      mixpanel.track(eventName, eventParams);

      // Update event stats (dynamic import to avoid circular dependency)
      import('./queueManagement').then(({ eventStats }) => {
        eventStats.successful++;
      });

      // Track success pattern (with additional safety)
      try {
        trackAnalyticsErrorPattern('mixpanel_tracking', true);
      } catch (patternError) {
        // Silently handle pattern tracking errors to avoid breaking the application
        debugLog('Pattern tracking error:', patternError);
      }
    } else {
      debugLog('Mixpanel not available for event:', eventName);
      // Queue event for later if Mixpanel hasn't loaded yet (dynamic import)
      import('./queueManagement').then(({ queueMixpanelEvent, eventStats }) => {
        queueMixpanelEvent(eventName, eventParams);
        eventStats.queued++;
      });
    }
  } catch (error) {
    debugLog('Mixpanel tracking failed:', error);

    // Update event stats (dynamic import to avoid circular dependency)
    import('./queueManagement').then(({ eventStats }) => {
      eventStats.failed++;
    });

    // Track failure pattern with additional safety
    try {
      trackAnalyticsErrorPattern('mixpanel_tracking', false);
    } catch (patternError) {
      // Silently handle pattern tracking errors to avoid breaking the application
      debugLog('Pattern tracking error in catch block:', patternError);
    }

    // Optionally queue for retry (with additional safety)
    try {
      import('./queueManagement').then(({ queueMixpanelEvent }) => {
        queueMixpanelEvent(eventName, eventParams);
      });
    } catch (queueError) {
      debugLog('Queue error:', queueError);
    }
  }
};

// Track event in Clarity
const trackClarityEvent = (eventName, _eventParams) => {
  try {
    if (
      typeof Clarity !== 'undefined' &&
      Clarity &&
      typeof Clarity.event === 'function'
    ) {
      Clarity.event(eventName);
      debugLog('✅ Clarity event tracked:', eventName);
      // Track success pattern
      trackAnalyticsErrorPattern('clarity_tracking', true);
    } else {
      debugLog('⚠️ Clarity not available for event:', eventName);
      // Track unavailability pattern
      trackAnalyticsErrorPattern('clarity_availability', false);
    }
  } catch (error) {
    debugLog('❌ Clarity tracking failed:', error);
    // Track failure pattern and log critical errors for frequent failures
    trackAnalyticsErrorPattern('clarity_tracking', false);

    // Send critical Clarity failures to Netlify function for monitoring
    logCriticalAnalyticsError(
      'clarity_tracking_failed',
      `Clarity event tracking failed: ${error.message}`,
      {
        eventName,
        error: error.message,
        stack: error.stack,
        clarityAvailable: typeof Clarity !== 'undefined',
        clarityObject: !!Clarity,
        hasEventMethod: Clarity && typeof Clarity.event === 'function',
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      },
      'warning' // Lower severity than Google Analytics since Clarity is less critical
    );
  }
};
